import { InfoOutline } from '@mui/icons-material';
import {
  FormControlLabel,
  Radio,
  RadioGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { HoldingClassSelectionType } from 'further-types/lp/investment';
import { CurrencyCode } from 'further-types/lp/vehicle';
import BasicSelect from 'further-ui/components/BasicSelect';
import FormField from 'further-ui/components/FormField';
import { Column, Row } from 'further-ui/components/Layout';
import { investment as rules } from 'further-ui/validations';
import { FC } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

interface HoldingClassSelectionProps {
  index: number;
  holdingClassOptions: Array<{
    id: string;
    title: string;
    currency: CurrencyCode;
  }>;
  resetHoldingClassDropdown: (index: number) => void;
  isHoldingClassSelectDisabled: boolean;
}

const HoldingClassSelection: FC<HoldingClassSelectionProps> = ({
  index,
  holdingClassOptions,
  resetHoldingClassDropdown,
  isHoldingClassSelectDisabled,
}) => {
  const { control, setValue, getValues } =
    useFormContext<rules.CreateInvestmentFormValues>();

  const handleHoldingClassChange = (value: string) => {
    const selectedClass = holdingClassOptions.find(
      (option) => option.id === value,
    );
    if (selectedClass) {
      setValue(`holdingClasses.${index}.holdingClassId`, selectedClass.id);
      setValue(`holdingClasses.${index}.name`, selectedClass.title);
      setValue(`holdingClasses.${index}.currency`, selectedClass.currency);
      setValue('currencyRate', undefined);
    }
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const isNew = e.target.value === HoldingClassSelectionType.NEW;
    setValue(`holdingClasses.${index}.isNewHoldingClass`, isNew);
    setValue('currencyRate', undefined);
    const baseCurrency = getValues('asset.baseCurrency');

    if (isNew) {
      setValue(`holdingClasses.${index}.holdingClassId`, '');
      setValue(`holdingClasses.${index}.currency`, baseCurrency);
      setValue(`holdingClasses.${index}.name`, '');
    }
  };

  return (
    <Controller
      name={`holdingClasses.${index}.isNewHoldingClass`}
      control={control}
      render={({ field: { value, ...rest } }) => (
        <RadioGroup
          {...rest}
          value={
            value === null || value === undefined
              ? ''
              : value
              ? HoldingClassSelectionType.NEW
              : HoldingClassSelectionType.EXISTING
          }
          onChange={handleRadioChange}
        >
          <Column spacing="lg">
            <FormField
              label={
                <FormControlLabel
                  value={HoldingClassSelectionType.EXISTING}
                  control={<Radio disabled={!holdingClassOptions.length} />}
                  label={
                    <Typography variant="h6">
                      Select existing holding class
                    </Typography>
                  }
                  disabled={!holdingClassOptions.length}
                />
              }
              size="md"
            >
              <Controller
                name={`holdingClasses.${index}.holdingClassId`}
                control={control}
                render={({
                  field: { ref, onChange: fieldOnChange, ...rest },
                  fieldState: { error },
                }) => (
                  <BasicSelect
                    {...rest}
                    fullWidth
                    options={holdingClassOptions.map((option) => ({
                      value: option.id,
                      label: option.title,
                    }))}
                    placeholder="Select existing holding class"
                    error={!!error}
                    helperText={error?.message}
                    ref={ref}
                    disabled={isHoldingClassSelectDisabled}
                    key={`holding-class-select-${index}-${holdingClassOptions.length}`}
                    onChange={(newValue) => {
                      fieldOnChange(newValue);
                      handleHoldingClassChange(newValue);
                    }}
                  />
                )}
              />
            </FormField>

            <FormField size="md">
              <Row centered spacing="xxs">
                <FormControlLabel
                  value={HoldingClassSelectionType.NEW}
                  control={<Radio />}
                  label={
                    <Typography variant="h6">
                      Create new holding class
                    </Typography>
                  }
                  onClick={() => {
                    resetHoldingClassDropdown(index);
                  }}
                />
                <Tooltip title=" Select if the holding class is not yet in the system.">
                  <InfoOutline fontSize="inherit" />
                </Tooltip>
              </Row>
            </FormField>
          </Column>
        </RadioGroup>
      )}
    />
  );
};

export default HoldingClassSelection;
